import { Authentication } from '@/common/domain/auth/Authentication';
import { FanIdentity } from '@/common/domain/auth/FanIdentity';
import { Optional } from '@/common/domain/Optional';
import { ClubSlug } from '@/fairplayer/domain/club/ClubSlug';
import { Fan } from '@/fairplayer/domain/fan/Fan';
import { FanPersonalInfo } from '@/fairplayer/domain/fan/FanPersonalInfo';
import { FanRepository } from '@/fairplayer/domain/fan/FanRepository';
import { BackendFan } from '@/fairplayer/secondary/fan/BackendFan';
import { toFanPersonalInfo } from '@/fairplayer/secondary/fan/BackendFanPersonalInfo';
import { BackendFanRepository } from '@/fairplayer/secondary/fan/BackendFanRepository';
import { Club } from '@/fairplayer/domain/club/Club';
import { FanToCreate } from '@/fairplayer/domain/fan/FanToCreate';
import { FanToUpdate } from '@/fairplayer/domain/fan/FanToUpdate';
import { CardPaymentMethod } from '@/fairplayer/domain/fan/CardPaymentMethod';
import { CheckoutSession } from '@/fairplayer/domain/CheckoutSession';
import { Url } from '@/common/domain/Url';
import { Username } from '@/common/domain/Username';

const extractIdentity = (
  personalInfo: Optional<FanPersonalInfo>,
  backendPictureUrl: string | null,
  authenticatedFan: FanIdentity
): FanIdentity => {
  const pictureUrl = Optional.ofUndefinable(backendPictureUrl).or(authenticatedFan.pictureUrl);

  return personalInfo
    .map(info => ({
      firstName: info.firstName,
      lastName: info.lastName,
      name: `${info.firstName} ${info.lastName}`,
      username: authenticatedFan.username,
      email: authenticatedFan.email,
      pictureUrl,
    }))
    .orElse({ ...authenticatedFan, pictureUrl });
};

const toFan = (fan: BackendFan, authenticatedFan: FanIdentity): Fan => {
  const { kycStatus, pictureUrl } = fan;
  const personalInfo = toFanPersonalInfo(fan.personalInfo);
  const identity = extractIdentity(personalInfo, pictureUrl, authenticatedFan);

  return {
    id: fan.identifier.id,
    identity,
    kycStatus,
    personalInfo,
  };
};

export class FairplayerFanRepository implements FanRepository {
  constructor(
    private backendFanRepository: BackendFanRepository,
    private authentication: Authentication
  ) {}

  async registerFan(fanToCreate: FanToCreate): Promise<Fan> {
    return Promise.all([this.backendFanRepository.registerFan(fanToCreate), this.authentication.authenticatedFan()]).then(
      ([backendFan, authenticatedFan]) => toFan(backendFan, authenticatedFan)
    );
  }

  async getForClub(club: Club, forceRefresh = false): Promise<Fan> {
    return Promise.all([this.getFan(club, forceRefresh), this.authentication.authenticatedFan()]).then(([backendFan, authenticatedFan]) =>
      toFan(backendFan, authenticatedFan)
    );
  }

  private async getFan(club: Club, forceRefresh: boolean): Promise<BackendFan> {
    return await this.backendFanRepository.getFan(club.slug, forceRefresh);
  }

  update(fanToUpdate: FanToUpdate): Promise<void> {
    return this.backendFanRepository.update(fanToUpdate);
  }

  updateLanguage(clubSlug: ClubSlug, username: Username): Promise<void> {
    return this.backendFanRepository.updateLanguage(clubSlug, username);
  }

  async sendPersonalInfoFor(fanInfo: FanPersonalInfo, clubSlug: ClubSlug): Promise<Fan> {
    const authenticatedFan = await this.authentication.authenticatedFan();
    const backendFan = await this.backendFanRepository.sendPersonalFanInfoFor(fanInfo, clubSlug, authenticatedFan.username);

    return toFan(backendFan, authenticatedFan);
  }

  acceptTos(clubSlug: ClubSlug): Promise<void> {
    return this.backendFanRepository.acceptTos(clubSlug);
  }

  async setCardPaymentMethodFor(clubSlug: ClubSlug, callbackUrl: Url): Promise<CheckoutSession> {
    const authenticatedFan = await this.authentication.authenticatedFan();
    return await this.backendFanRepository.setCardPaymentMethodFor(clubSlug, authenticatedFan.username, callbackUrl);
  }

  async getCardPaymentMethodFor(clubSlug: ClubSlug): Promise<CardPaymentMethod> {
    const authenticatedFan = await this.authentication.authenticatedFan();
    return await this.backendFanRepository.getCardPaymentMethodFor(clubSlug, authenticatedFan.username);
  }
}
